/**
 * The payment-gateway contract. Isomorphic and dependency-free (no db, no SDK,
 * no server-only imports) so client components can read ids and capabilities
 * from the same source the server implementations do.
 *
 * A GATEWAY is an external system that takes money and, optionally, owns a
 * recurring subscription: it hosts the checkout, holds the payment method,
 * schedules renewals, and is what the reconcile backstop compares our state
 * against. Stripe is one. Everything the gateway-agnostic core needs from a
 * gateway is on `PaymentGateway` below, and nothing else in `lib/billing` may
 * import a gateway's SDK or its modules directly — go through the registry
 * (`registry.ts`), which is the single place gateway ids resolve to code.
 *
 * NOT on this interface, on purpose:
 *
 *  - Webhook handling. Each gateway keeps its own route → pure decisions module
 *    → idempotent applier chain, because the event shapes have nothing in
 *    common; forcing them behind one `verifyWebhook(): unknown` would erase the
 *    types that make those modules testable. The APPLIERS converge instead, on
 *    the gateway-neutral writers in `plan-cycle.server.ts` /
 *    `purchase-data.server.ts`.
 *  - Anything optional a gateway may not have. Crypto rails cannot auto-charge,
 *    have no chargebacks, and host no management portal, so `refundPayment`,
 *    `cancelSubscription`, `createManagementSession`, `reconcile*` and
 *    `checkConfiguration` are all optional and gated by `capabilities`.
 */

import type {
  BillingMode,
  PaymentRail,
  PricingPlan,
} from '@/lib/billing/billing'
import type { CheckoutCoupon } from '@/lib/billing/coupons'

/**
 * Every payment gateway the app can route money through. Adding one means: a
 * value here, a value in the `funding_source` DB enum, an entry in
 * `GATEWAY_CAPABILITIES` and `GATEWAY_ENV_FLAG`, and a registry entry. The
 * union is deliberately narrow so TypeScript flags each of those omissions.
 */
export const GATEWAY_IDS = ['stripe', 'cryptomus'] as const

export type GatewayId = (typeof GATEWAY_IDS)[number]

/**
 * Whether a payment rail names a real GATEWAY (which holds external objects)
 * rather than the in-house 'direct' rail no gateway has ever heard of. One
 * half of `gatewayOwnsSubscription` — see BILLING_MODEL_DESIGN §1.3.
 */
export function isGatewayId(
  value: PaymentRail | string | null | undefined,
): value is GatewayId {
  return (GATEWAY_IDS as readonly string[]).includes(value ?? '')
}

/**
 * What a gateway can actually do. Read by the UI (which CTA to render) and by
 * the server (which optional method it may call). Static per gateway — a
 * capability that varies at runtime belongs in `isEnabled()`, not here.
 */
export interface GatewayCapabilities {
  /** Can host a recurring subscription checkout and bill it on a schedule. */
  subscriptions: boolean
  /**
   * Can sell a FIXED-LENGTH term paid up front, which our own scheduler then
   * renews. Deliberately separate from `subscriptions`: crypto can take the
   * money but can never auto-charge month two, and conflating the two would
   * let `gatewayFor('subscriptions')` hand a plain "Subscribe" click to a rail
   * that cannot renew.
   */
  prepaidTerms: boolean
  /** Can take a one-off payment for a data top-up. */
  topUps: boolean
  /** Hosts a customer-facing portal for payment methods / cancellation. */
  hostedManagement: boolean
  /** Can return money programmatically (needed by the top-up auto-refund). */
  refunds: boolean
  /** Exposes enough read APIs to replay missed webhooks as a backstop. */
  reconcile: boolean
}

export const GATEWAY_CAPABILITIES: Record<GatewayId, GatewayCapabilities> = {
  stripe: {
    subscriptions: true,
    prepaidTerms: true,
    topUps: true,
    hostedManagement: true,
    refunds: true,
    reconcile: true,
  },
  cryptomus: {
    // No auto-charge: a crypto payment is a one-off, so month two needs the
    // customer to pay again. Terms yes, subscriptions never.
    subscriptions: false,
    prepaidTerms: true,
    topUps: true,
    // No customer portal, and no programmatic refund: sending crypto back to
    // the payer's `from` address destroys funds when it is an exchange hot
    // wallet, so refunds are an operator action (CRYPTOMUS_DESIGN §2.2).
    hostedManagement: false,
    refunds: false,
    // Load-bearing here, not belt-and-braces: Cryptomus promises no automatic
    // webhook retry, so this pass is how a dropped callback is recovered at
    // all. Backed by the `cryptomus_invoices` work list.
    reconcile: true,
  },
}

/** The env var that kill-switches each gateway. Read isomorphically (see
 *  `availability.ts`). */
export const GATEWAY_ENV_FLAG: Record<GatewayId, string> = {
  stripe: 'STRIPE_ENABLED',
  cryptomus: 'CRYPTOMUS_ENABLED',
}

/**
 * What an UNSET flag means, per gateway. Stripe launched default-ON (its env
 * var is a kill-switch: 'false' disables, unset runs). A NEW rail must be
 * default-OFF — otherwise the release that merely adds its code turns it on in
 * every deploy whose env predates the flag.
 */
export const GATEWAY_DEFAULT_ENABLED: Record<GatewayId, boolean> = {
  stripe: true,
  cryptomus: false,
}

// ---------------------------------------------------------------------------
// Call arguments and results
// ---------------------------------------------------------------------------

/** A hosted page the customer is redirected to. */
export interface HostedSession {
  url: string
}

export interface SubscriptionCheckoutArgs {
  organizationId: string
  /** The purchasing admin's email, for the gateway's customer record. */
  email: string
  /** The plan being subscribed to, already resolved against the org's own
   *  catalog view (so a negotiated plan checks out at ITS price). */
  plan: PricingPlan
  /**
   * A coupon the customer entered, ALREADY validated and priced by
   * `coupons.server.ts`. The gateway's job is to make its own checkout charge
   * the discounted amount — for a rail that owns the subscription that means
   * attaching a discount object the rail holds, so renewals stay discounted
   * too, not simply invoicing less this once.
   */
  coupon?: CheckoutCoupon | null
  /**
   * Start the subscription with a free trial: no charge until `days` have
   * passed, a payment method still collected. The gateway stamps `userId`
   * and `allowanceBytes` on the subscription so the first ($0) invoice can
   * grant the trial allowance and record the claim.
   */
  trial?: {
    days: number
    userId: string
    email: string
    allowanceBytes: number
  }
  successUrl: string
  cancelUrl: string
}

/**
 * A fixed-length term paid up front. Separate from `SubscriptionCheckoutArgs`
 * because the LENGTH is the thing being bought — with no auto-renewal, "how
 * many months" is a customer choice at checkout rather than an implicit month.
 */
export interface TermCheckoutArgs {
  organizationId: string
  email: string
  plan: PricingPlan
  /** Whole months being bought, 1-12. */
  months: number
  /** Validated coupon, applied to the WHOLE term total (a term is one payment,
   *  so a coupon's Stripe-shaped duration has nothing to repeat over). */
  coupon?: CheckoutCoupon | null
  successUrl: string
  cancelUrl: string
}

export interface TopUpCheckoutArgs {
  organizationId: string
  email: string
  /** Whole GB being bought — for the line-item description. */
  gb: number
  /** Total price in micro-USD, AFTER any coupon. The gateway converts to its
   *  own unit. */
  amountMicroUsd: number
  /**
   * The per-GB rate (micro-USD) this checkout was priced at. The gateway must
   * carry it through to settlement so the credit path funds the org at the
   * price the customer SAW, even if the catalog changes in between.
   *
   * On a DISCOUNTED top-up this is the discounted effective rate
   * (`amountMicroUsd / gb`), not the catalog rate — settlement derives bytes as
   * `paid / rate`, so stamping the catalog rate against a discounted payment
   * would quietly hand the customer fewer GB than they bought. See
   * `createPurchaseDataCheckout`.
   */
  pricePerGbMicroUsd: number
  /** Validated coupon, already reflected in `amountMicroUsd` and
   *  `pricePerGbMicroUsd`. Carried so settlement can record the redemption. */
  coupon?: CheckoutCoupon | null
  successUrl: string
  cancelUrl: string
}

export interface ManagementSessionArgs {
  organizationId: string
  returnUrl: string
}

export interface RefundArgs {
  /**
   * The gateway's handle for the payment being refunded — the same value the
   * credit was keyed on (`ledger_entries.external_ref`). Opaque to the caller.
   */
  paymentRef: string
  /** Amount to hand back, in micro-USD. Must not exceed what was paid. */
  amountMicroUsd: number
  /** Machine-readable reason, also used to key the refund idempotently. */
  reason: string
}

export interface CancelSubscriptionArgs {
  organizationId: string
  subscriptionRef: string
  /**
   * Credit the unused remainder of the current period back to the customer
   * instead of keeping the full charge. Used when cancelling a subscription a
   * newer one superseded, so the org isn't double-billed with value destroyed.
   */
  prorate?: boolean
}

/**
 * `'cancelled'` — we cancelled it. `'already-gone'` — the gateway has no live
 * subscription under that ref (already cancelled, or deleted), which is the
 * EXPECTED outcome when a grant supersedes a stale-but-dead subscription and
 * must not page anyone. Anything else throws.
 */
export type CancelOutcome = 'cancelled' | 'already-gone'

// ---------------------------------------------------------------------------
// Reconciliation (the gateway-neutral shape of a backstop pass)
// ---------------------------------------------------------------------------

export type ReconcileKind =
  | 'planGrant'
  | 'topUp'
  | 'chargeReversal'
  | 'dispute'
  | 'ghostSubscription'
  | 'multipleLiveSubscriptions'
  | 'planSync'
  | 'missingCustomer'

/** `healed` = a missed webhook was safely replayed; `alarmed` = a divergence we
 *  would NOT auto-mutate, surfaced for a human; `skipped` = detected but a
 *  guard/no-op made it a non-event. */
export type ReconcileAction = 'healed' | 'alarmed' | 'skipped'

export interface ReconcileFinding {
  organizationId: string
  kind: ReconcileKind
  action: ReconcileAction
  detail: string
}

export interface ReconcileSummary {
  /** True when the gateway isn't configured (dev/test) — nothing ran. */
  skipped: boolean
  orgsChecked: number
  healed: number
  alarmed: number
  /** Per-org failures. A pass continues past them — one org's failure never
   *  aborts the sweep. */
  errors: { organizationId: string; message: string }[]
  findings: ReconcileFinding[]
}

/** An empty pass, for callers that need a summary without running one. */
export function emptyReconcileSummary(skipped = false): ReconcileSummary {
  return {
    skipped,
    orgsChecked: 0,
    healed: 0,
    alarmed: 0,
    errors: [],
    findings: [],
  }
}

/** Merge per-gateway passes into one summary for the maintenance report. */
export function mergeReconcileSummaries(
  summaries: ReconcileSummary[],
): ReconcileSummary {
  return {
    // Skipped only when EVERY gateway skipped: one gateway being unconfigured
    // must not make a pass that did real work read as "nothing ran".
    skipped: summaries.length > 0 && summaries.every((s) => s.skipped),
    orgsChecked: summaries.reduce((n, s) => n + s.orgsChecked, 0),
    healed: summaries.reduce((n, s) => n + s.healed, 0),
    alarmed: summaries.reduce((n, s) => n + s.alarmed, 0),
    errors: summaries.flatMap((s) => s.errors),
    findings: summaries.flatMap((s) => s.findings),
  }
}

// ---------------------------------------------------------------------------
// Admin diagnostics ("our record vs the gateway's")
// ---------------------------------------------------------------------------

/** Our stored billing state for one org, handed to a gateway so it can compare
 *  without re-reading the DB. */
export interface OrgBillingRecord {
  organizationId: string
  planSlug: string | null
  subscriptionRef: string | null
  billingMode: BillingMode
  paymentRail: PaymentRail
  planPeriodEnd: Date | null
}

/** A subscription as the gateway reports it, trimmed for the admin view. */
export interface GatewaySubscriptionSummary {
  id: string
  status: string
  /** Plan slug as the GATEWAY understands it, which is what makes a
   *  portal-initiated plan switch visible before our webhook lands. */
  planSlug: string | null
  /** Monthly amount in cents. */
  amountCents: number | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  createdAt: string
}

/**
 * What one gateway says about one org, next to what we stored. The
 * `discrepancies` are written for the admin reading them — each one names a
 * concrete divergence and what to do about it.
 */
export interface GatewayOrgDiagnostics {
  gatewayId: GatewayId
  /** Display name for the rail. */
  label: string
  /** False when the org HAS an account on this rail but it could not be
   *  queried, so the admin sees "unreachable" instead of a falsely clean card.
   *  ("No account on this rail" is encoded by returning null, not by a flag.) */
  reachable: boolean
  /** Deep link into the rail's own dashboard for this org, when there is one. */
  dashboardUrl: string | null
  subscriptions: GatewaySubscriptionSummary[]
  discrepancies: string[]
}

// ---------------------------------------------------------------------------
// Configuration assertions
// ---------------------------------------------------------------------------

/**
 * Result of a read-only, ALARM-ONLY assertion that a gateway's out-of-repo
 * configuration still matches what the billing math assumes (for Stripe: the
 * customer portal's plan-switch settings). Never mutates the gateway.
 */
export interface GatewayConfigCheck {
  /** True when the gateway isn't configured (dev/test) — nothing ran. */
  skipped: boolean
  /** True when a configuration was found and evaluated. */
  checked: boolean
  /** Human-readable misconfiguration problems (empty = healthy). */
  problems: string[]
}

// ---------------------------------------------------------------------------
// The interface
// ---------------------------------------------------------------------------

export interface PaymentGateway {
  readonly id: GatewayId
  readonly capabilities: GatewayCapabilities

  /** Runtime kill-switch. False means every method here refuses; the core
   *  checks this before dispatching so the UI and the server agree. */
  isEnabled(): boolean

  /** Hosted checkout that subscribes the org to a monthly plan. The gateway
   *  owns customer creation/reuse and must stamp enough context on its own
   *  objects for its webhook to attribute the payment without trusting the
   *  client. Required — a gateway with `capabilities.subscriptions` false
   *  throws a `PublicError` here rather than pretending. */
  createSubscriptionCheckout(
    args: SubscriptionCheckoutArgs,
  ): Promise<HostedSession>

  /** Hosted checkout for a one-off data top-up. Same contract as above. */
  createTopUpCheckout(args: TopUpCheckoutArgs): Promise<HostedSession>

  /** Hosted checkout for a fixed-length prepaid term. Present only when
   *  `capabilities.prepaidTerms`. */
  createTermCheckout?(args: TermCheckoutArgs): Promise<HostedSession>

  /** Hosted portal for payment methods, invoices and cancellation.
   *  Present only when `capabilities.hostedManagement`. */
  createManagementSession?(args: ManagementSessionArgs): Promise<HostedSession>

  /** Hand money back. Present only when `capabilities.refunds`. Must be
   *  idempotent on (paymentRef, reason) so a webhook retry cannot refund
   *  twice. */
  refundPayment?(args: RefundArgs): Promise<void>

  /** Stop a subscription billing. Present only when
   *  `capabilities.subscriptions`. Returns `'already-gone'` rather than
   *  throwing when the gateway has nothing live under that ref. */
  cancelSubscription?(args: CancelSubscriptionArgs): Promise<CancelOutcome>

  /**
   * Credit the gateway-side customer balance (a GIFT: admin gives N months of
   * plan price, the gateway's next invoices consume it and still fire the
   * normal paid events, so the grant machinery needs no special case). Present
   * only on gateways with a customer-balance concept.
   */
  creditCustomerBalance?(args: {
    organizationId: string
    amountMicroUsd: number
    description: string
    idempotencyKey: string
  }): Promise<void>

  /** Replay this org's missed events through the same idempotent appliers the
   *  webhook uses. Present only when `capabilities.reconcile`. */
  reconcileOrganization?(organizationId: string): Promise<ReconcileSummary>

  /** The same, for every org this gateway funds. */
  reconcileAll?(): Promise<ReconcileSummary>

  /** Assert the gateway's out-of-repo configuration. Read-only, and the
   *  implementation OWNS its own alarming — the sweep that calls this only
   *  aggregates counts, so exactly one page is raised per misconfiguration. */
  checkConfiguration?(): Promise<GatewayConfigCheck>

  /**
   * What this gateway holds for one org, and how it diverges from our record —
   * the admin billing card's "our record vs theirs" panel. Read-only. Returns
   * null when the org has no account on this rail, so the card shows only the
   * rails that actually apply to it.
   */
  describeOrgSubscriptions?(
    record: OrgBillingRecord,
  ): Promise<GatewayOrgDiagnostics | null>
}
